import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { toast } from 'sonner';
import {
  LEAD_PRODUTO_VALORES,
  PRODUTO_LEAD_PARCERIA_CRM,
  leadProdutoBadgeMeta,
} from '@/constants/leadsProduto';

type IrmaoLead = {
  id: string;
  produto: string | null;
  nome?: string | null;
  razao_social?: string | null;
  tipo?: string | null;
  is_atual?: boolean;
  tem_proposta?: boolean;
};

type ProdutosInteressadosProps = {
  leadId: string;
  /** Produto do lead aberto — sempre listado. */
  produtoAtual?: string | null;
  /** Quando true, permite adicionar/remover produtos irmãos. */
  canEdit?: boolean;
  onChanged?: () => void;
};

const catalogoProdutos = LEAD_PRODUTO_VALORES.filter((p) => p !== PRODUTO_LEAD_PARCERIA_CRM);

export default function ProdutosInteressados({
  leadId,
  produtoAtual,
  canEdit = false,
  onChanged,
}: ProdutosInteressadosProps) {
  const [loading, setLoading] = useState(true);
  const [irmaos, setIrmaos] = useState<IrmaoLead[]>([]);
  const [tipoLead, setTipoLead] = useState<'fisica' | 'juridica' | null>(null);
  const [temDocumento, setTemDocumento] = useState(false);
  const [produtoNovo, setProdutoNovo] = useState('');
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const docLabel = tipoLead === 'juridica' ? 'CNPJ' : 'CPF';

  const loadIrmaos = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchWithAuth(`/api/leads/${leadId}/irmaos`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Erro ao carregar produtos');
      }
      const data = await res.json();
      const tipo =
        data.tipo === 'juridica' || data.tipo === 'fisica'
          ? data.tipo
          : Array.isArray(data.irmaos) && data.irmaos[0]?.tipo === 'juridica'
            ? 'juridica'
            : 'fisica';
      setTipoLead(tipo);
      setTemDocumento(
        tipo === 'juridica'
          ? !!(data.cnpj_normalizado || data.cnpj)
          : !!(data.cpf_normalizado || data.cpf)
      );
      setIrmaos(Array.isArray(data.irmaos) ? data.irmaos : []);
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Erro ao carregar produtos interessados');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    if (leadId) loadIrmaos();
  }, [leadId, loadIrmaos]);

  const produtosJaCadastrados = new Set(
    irmaos.map((i) => i.produto).filter((p): p is string => !!p)
  );
  const opcoesDisponiveis = catalogoProdutos.filter((p) => !produtosJaCadastrados.has(p));

  const handleAdd = async () => {
    if (!produtoNovo) {
      toast.error('Selecione um produto');
      return;
    }
    if (!temDocumento) {
      toast.error(
        `Informe o ${docLabel} neste cadastro antes de adicionar outro produto.`
      );
      return;
    }
    try {
      setSaving(true);
      const res = await fetchWithAuth(`/api/leads/${leadId}/clone-produto`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ produto: produtoNovo }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Erro ao adicionar produto');
      }
      toast.success('Produto adicionado — nova negociação criada no CRM');
      setProdutoNovo('');
      await loadIrmaos();
      onChanged?.();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Erro ao adicionar produto');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (irmao: IrmaoLead) => {
    if (irmao.is_atual) {
      toast.error('Não é possível remover o produto desta negociação por aqui. Use excluir lead se necessário.');
      return;
    }
    if (irmao.tem_proposta) {
      toast.error('Este produto já possui proposta e não pode ser removido.');
      return;
    }
    try {
      setRemovingId(irmao.id);
      const res = await fetchWithAuth(`/api/leads/${irmao.id}`, { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Erro ao remover produto');
      }
      toast.success('Produto removido');
      await loadIrmaos();
      onChanged?.();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Erro ao remover produto');
    } finally {
      setRemovingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        Carregando produtos…
      </div>
    );
  }

  return (
    <div className="space-y-3 col-span-2">
      <div>
        <label className="text-sm font-medium text-muted-foreground block">
          Produtos interessados
        </label>
        <p className="text-xs text-muted-foreground mt-0.5">
          Cada produto é uma negociação separada no CRM
          {produtoAtual ? ` · este cadastro: ${produtoAtual}` : ''}.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {irmaos.length === 0 ? (
          <span className="text-sm text-muted-foreground">Nenhum produto listado</span>
        ) : (
          irmaos.map((irmao) => {
            const meta = leadProdutoBadgeMeta(irmao.produto);
            return (
              <div
                key={irmao.id}
                className="inline-flex items-center gap-1.5 rounded-md border px-2 py-1"
              >
                <Badge
                  variant="outline"
                  className={`text-xs ${meta?.badgeClassName ?? ''}`}
                >
                  {meta?.label ?? irmao.produto ?? '—'}
                </Badge>
                {irmao.is_atual && (
                  <span className="text-[10px] uppercase text-muted-foreground">atual</span>
                )}
                {irmao.tem_proposta && (
                  <span className="text-[10px] text-muted-foreground">com proposta</span>
                )}
                {canEdit && !irmao.is_atual && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    disabled={removingId === irmao.id || !!irmao.tem_proposta}
                    title={
                      irmao.tem_proposta
                        ? 'Possui proposta — não pode remover'
                        : 'Remover este produto'
                    }
                    onClick={() => handleRemove(irmao)}
                  >
                    {removingId === irmao.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Trash2 className="h-3 w-3 text-destructive" />
                    )}
                  </Button>
                )}
              </div>
            );
          })
        )}
      </div>

      {canEdit && (
        <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end">
          <div className="flex-1 space-y-1">
            <label className="text-xs text-muted-foreground">Adicionar produto</label>
            <Select value={produtoNovo} onValueChange={setProdutoNovo}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione um produto" />
              </SelectTrigger>
              <SelectContent>
                {opcoesDisponiveis.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            type="button"
            variant="secondary"
            disabled={saving || !produtoNovo || opcoesDisponiveis.length === 0}
            onClick={handleAdd}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            Adicionar
          </Button>
        </div>
      )}

      {canEdit && !temDocumento && (
        <p className="text-xs text-amber-700">
          Cadastre o {docLabel} para poder vincular outros produtos a este cliente.
        </p>
      )}
    </div>
  );
}
